import React, { useState, useEffect } from "react";
import { SimpleSelect } from "./common/SimpleSelect";
import { getYear } from "date-fns";
import _ from "lodash";
import { getMonthAbbreviation } from "../services/dateService";
import { getDrugList } from "../services/dashApiService";
import { Autocomplete } from "./common/Autocomplete";
import { QuestionnaireHeader } from "./QuestionnaireHeader";
import { QuestionnaireFooter } from "./QuestionnaireFooter";
import { toast } from "react-toastify";

export const QuestionnaireDrugTreatmentPage = ({
	def,
	onRespond,
	currentResponse,
}) => {
	const [selectedOption, setSelectedOption] = useState({
		option: "",
		startDate: 0,
		endDate: 0,
	});

	const [currentStartMonthOption, setCurrentStartMonthOption] = useState(null);
	const [currentStartYearOption, setCurrentStartYearOption] = useState(null);
	const [currentEndMonthOption, setCurrentEndMonthOption] = useState(null);
	const [currentEndYearOption, setCurrentEndYearOption] = useState(null);
	const [drugs, setDrugs] = useState([]);
	const [drugsObj, setDrugsObj] = useState();
	const [isLoading, setIsLoading] = useState(true);

	const years = _.range(1950, getYear(Date.now()) + 1);
	const [yearsOptions] = useState(
		years.map((y) => {
			return {
				id: y,
				label: y,
				value: y,
			};
		})
	);

	const months = _.range(12);
	const [monthsOptions] = useState(
		months.map((m) => {
			return {
				id: m,
				label: getMonthAbbreviation(m),
				value: m,
			};
		})
	);

	const deleteStartDate = () => {
		const _selectedOption = { ...selectedOption };
		_selectedOption.startDate = 0;

		const response = {
			id: def.id,
			value: _selectedOption,
		};

		onRespond(response);
	};

	const deleteEndDate = () => {
		const _selectedOption = { ...selectedOption };
		_selectedOption.endDate = 0;

		const response = {
			id: def.id,
			value: _selectedOption,
		};

		onRespond(response);
	};

	const onAutoCompleteChange = (selection, actionMeta) => {
		const _selectedOption = { ...selectedOption };
		let value;

		if (def?.params?.isMulti) {
			value = _selectedOption.option || [];
			switch (actionMeta.action) {
				case "select-option":
					if (value.indexOf(actionMeta.option.value < 0)) {
						value.push(actionMeta.option.value);
					}
					break;

				case "create-option":
					if (value.indexOf(actionMeta.option.value < 0)) {
						value.push(actionMeta.option.value);
					}
					break;

				case "remove-value":
					const index = value.indexOf(actionMeta?.removedValue?.label);
					if (index > -1) {
						value.splice(index, 1);
					}
					break;

				case "clear":
					value = "";
					break;

				default:
					return;
			}
		} else {
			switch (actionMeta.action) {
				case "select-option":
					value = selection.value;
					break;

				case "create-option":
					value = selection.value;
					break;

				case "clear":
					value = "";
					break;

				default:
					return;
			}
		}

		_selectedOption.option = value;

		const response = {};
		response.id = def.id;
		response.value = _selectedOption;

		onRespond(response);
	};

	const initDate = (dt, mod) => {
		const date = dt ? new Date(dt) : new Date();
		date.setHours(0, 0, 0, 0);
		date.setDate(14);

		if (mod) {
			if (mod.month != null) {
				date.setMonth(mod.month);
			}

			if (mod.year != null) {
				date.setFullYear(mod.year);
			}
		}

		const nextMonth = getStartOfNextMonth();
		if (nextMonth.valueOf() < date.valueOf()) {
			date.setFullYear(date.getFullYear() - 1);
		}

		return date;
	};

	const getStartOfNextMonth = () => {
		const date = new Date();
		date.setHours(0, 0, 0, 0);
		date.setDate(1);
		date.setMonth(date.getMonth() + 1);

		return date;
	};

	const onChangeStartMonth = (sel) => {
		setCurrentStartMonthOption(sel);

		const date = initDate(selectedOption.startDate, { month: sel.value });

		const _selectedOption = { ...selectedOption };
		_selectedOption.startDate = date.valueOf();

		if (
			_selectedOption.startDate > _selectedOption.endDate &&
			_selectedOption.endDate
		) {
			_selectedOption.endDate = _selectedOption.startDate;
			toast(
				<>
					<p>The end date must be no earlier than the start date</p>
					<p>The end date has been reset</p>
				</>,
				{
					type: "warning",
				}
			);
		}

		const response = {
			id: def.id,
			value: _selectedOption,
		};
		onRespond(response);
	};

	const onChangeEndMonth = (sel) => {
		setCurrentEndMonthOption(sel);

		const date = initDate(selectedOption.endDate, { month: sel.value });

		const _selectedOption = { ...selectedOption };
		_selectedOption.endDate = date.valueOf();

		if (_selectedOption.endDate < _selectedOption.startDate) {
			_selectedOption.startDate = _selectedOption.endDate;
			toast(
				<>
					<p>The start date must be no later than the end date</p>
					<p>The start date has been reset</p>
				</>,
				{
					type: "warning",
				}
			);
		}

		const response = {
			id: def.id,
			value: _selectedOption,
		};
		onRespond(response);
	};

	const onChangeStartYear = (sel) => {
		setCurrentStartYearOption(sel);

		const date = initDate(selectedOption.startDate, { year: sel.value });

		const _selectedOption = { ...selectedOption };
		_selectedOption.startDate = date.valueOf();

		if (
			_selectedOption.endDate < _selectedOption.startDate &&
			_selectedOption.endDate
		) {
			_selectedOption.endDate = _selectedOption.startDate;
			toast(
				<>
					<p>The end date must be no earlier than the start date</p>
					<p>The end date has been reset</p>
				</>,
				{
					type: "warning",
				}
			);
		}

		const response = {
			id: def.id,
			value: _selectedOption,
		};
		onRespond(response);
	};

	const onChangeEndYear = (sel) => {
		setCurrentStartYearOption(sel);

		const date = initDate(selectedOption.endDate, { year: sel.value });

		const _selectedOption = { ...selectedOption };
		_selectedOption.endDate = date.valueOf();

		if (_selectedOption.startDate > _selectedOption.endDate) {
			_selectedOption.startDate = _selectedOption.endDate;
			toast(
				<>
					<p>The start date must be no later than the end date</p>
					<p>The start date has been reset</p>
				</>,
				{
					type: "warning",
				}
			);
		}

		const response = {
			id: def.id,
			value: _selectedOption,
		};
		onRespond(response);
	};

	useEffect(() => {
		const data = {};
		if (def && def.params) {
			data.purpose = def.params.purpose;
			data.diseaseGroup = def.params.diseaseGroup;
		}

		getDrugList(data)
			.then((result) => {
				const drugs = [];
				const drugsObj = {};
				for (const d of result.data.list) {
					drugs.push({ value: d.value, label: d.display });
					drugsObj[d.value] = { label: d.display };
				}

				setDrugs(drugs);
				setDrugsObj(drugsObj);
				setIsLoading(false);
			})
			.catch(() => {
				setIsLoading(false);
			});
	}, []);

	useEffect(() => {
		setSelectedOption({ option: "", startDate: 0, endDate: 0 });
	}, [def]);

	useEffect(() => {
		const processDate = (dt) => {
			const ret = {
				mnth: null,
				year: null,
			};

			if (dt) {
				const date = new Date(dt);
				const m = date.getMonth();
				ret.mnth = monthsOptions.find((month) => month.value === m);
				const y = date.getFullYear();
				ret.year = yearsOptions.find((year) => year.value === y);
			}

			return ret;
		};

		if (currentResponse && currentResponse.value) {
			const val = currentResponse.value;
			setSelectedOption(val);

			const sd = processDate(val.startDate);
			const ed = processDate(val.endDate);

			setCurrentStartMonthOption(sd.mnth);
			setCurrentStartYearOption(sd.year);
			setCurrentEndMonthOption(ed.mnth);
			setCurrentEndYearOption(ed.year);
		} else if (def.answer) {
			const val = def.answer;
			setSelectedOption(val);

			const sd = processDate(val.startDate);
			const ed = processDate(val.endDate);

			setCurrentStartMonthOption(sd.mnth);
			setCurrentStartYearOption(sd.year);
			setCurrentEndMonthOption(ed.mnth);
			setCurrentEndYearOption(ed.year);

			const response = {
				id: def.id,
				value: val,
			};
			onRespond(response);
		} else {
			setSelectedOption({ option: "", startDate: 0, endDate: 0 });
			setCurrentStartMonthOption(null);
			setCurrentStartYearOption(null);
			setCurrentEndMonthOption(null);
			setCurrentEndYearOption(null);
		}
	}, [currentResponse, yearsOptions, monthsOptions, def, onRespond]);

	let value = isLoading ? "" : selectedOption.option || "";
	if (def?.params?.isMulti) {
		if (selectedOption) {
			value = [];
			for (const o of selectedOption?.option) {
				if (drugsObj && drugsObj[o]) {
					value.push(drugsObj[o]);
				} else if (o) {
					value.push({ value: o, label: o });
				}
			}
		}
	} else {
		if (drugsObj && drugsObj[selectedOption.option]) {
			value = drugsObj[selectedOption.option];
		} else if (value.length > 0) {
			value = { value, label: value };
		}
	}

	return (
		<>
			<QuestionnaireHeader def={def} />

			<div className={"w-full pb-3 xs:pb-6 px-6 text-black text-centerenter "}>
				<Autocomplete
					id="startMonth"
					options={drugs}
					value={value}
					onChange={onAutoCompleteChange}
					placeholder="Drug - start typing for options"
					isClearable={true}
					isLoading={isLoading}
					allowCreate={def && def.params && def.params.allowCreate}
					isMulti={def?.params?.isMulti ? true : false}
					createLabel={def?.params?.createLabel}
					createLabelPosition={def?.params?.createLabelPosition}
				/>
			</div>

			<div className={"w-full mx-auto overflow-y-visible h-fit px-6 mt-2"}>
				<div className={"mb-2 w-full text-sm"}>
					<label className={"block w-full my-auto"} htmlFor={"date"}>
						Start
					</label>
					<div className="flex-grow  flex">
						<div className="w-full mr-2">
							<SimpleSelect
								id="startMonth"
								options={monthsOptions}
								value={currentStartMonthOption}
								onChange={onChangeStartMonth}
								placeholder="Month"
								isSearchable={false}
							/>
						</div>
						<div className="w-full">
							<SimpleSelect
								id="startYear"
								options={yearsOptions}
								value={currentStartYearOption}
								onChange={onChangeStartYear}
								placeholder="Year"
								isSearchable={false}
							/>
						</div>
						<div className="w-9">
							<button
								className="mx-2 cursor-pointer mt-1 p-1 text-center text-white bg-app-charcoal rounded-lg text-md block flex-shrink"
								onClick={deleteStartDate}
							>
								Clear
							</button>
						</div>
					</div>
				</div>
			</div>

			<div className={"w-full mx-auto overflow-y-visible h-fit px-6 mt-4 mt-2"}>
				<div className={"mb-2 w-full text-sm"}>
					<label className={"block w-full my-auto"} htmlFor={"date"}>
						End <i style={{ fontSize: "90%" }}>(if applicable)</i>
					</label>
					<div className="flex-grow  flex">
						<div className="w-full mr-2">
							<SimpleSelect
								id="endMonth"
								options={monthsOptions}
								value={currentEndMonthOption}
								onChange={onChangeEndMonth}
								placeholder="Month"
								isSearchable={false}
							/>
						</div>
						<div className="w-full">
							<SimpleSelect
								id="startYear"
								options={yearsOptions}
								value={currentEndYearOption}
								onChange={onChangeEndYear}
								placeholder="Year"
								isSearchable={false}
							/>
						</div>
						<div className="w-9">
							<button
								className="mx-2 cursor-pointer mt-1 p-1 text-center text-white bg-app-charcoal rounded-lg text-md block flex-shrink"
								onClick={deleteEndDate}
							>
								Clear
							</button>
						</div>
					</div>
				</div>
			</div>

			<QuestionnaireFooter def={def} />
		</>
	);
};
